import type { AxiosError } from "axios";
import { UPLOAD_MAX_ATTEMPTS, uploadRetryDelayMs } from "@arna-med/shared";

/**
 * True for failures a later attempt could plausibly succeed on.
 *
 * No response at all means the request never completed — offline, DNS failure,
 * a dropped connection mid-upload — which is exactly the case this task is
 * about. A 4xx (other than the "come back later" codes) is the server rejecting
 * the request itself, so retrying only wastes the doctor's bandwidth.
 */
export function isRetriableNetworkError(err: unknown): boolean {
  const status = (err as AxiosError)?.response?.status;
  if (status === undefined) return true;
  return status === 408 || status === 425 || status === 429 || status >= 500;
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

interface RetryOptions {
  maxAttempts?: number;
  /** Called before each wait, so the UI can show "reconnecting (2/5)". */
  onRetry?: (attempt: number, delayMs: number, err: unknown) => void;
  shouldRetry?: (err: unknown) => boolean;
}

/**
 * Run `fn` until it succeeds or the attempts run out, backing off between
 * tries. The last error is rethrown so callers can surface a real failure.
 */
export async function withNetworkRetry<T>(
  fn: () => Promise<T>,
  { maxAttempts = UPLOAD_MAX_ATTEMPTS, onRetry, shouldRetry = isRetriableNetworkError }: RetryOptions = {},
): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt === maxAttempts || !shouldRetry(err)) break;
      const delay = uploadRetryDelayMs(attempt);
      onRetry?.(attempt, delay, err);
      await sleep(delay);
    }
  }
  throw lastErr;
}
